<template>
  <div>
    <GeojsonLayer
      :before="before"
      :layer="layerStyle"
      :sourceId="SOURCE_ID"
      :source="buildingSource"
      @layerClicked="onClick"
      @layerMouseEnter="onMouseEnter"
      @layerMouseLeave="onMouseLeave"
      @layerMouseMove="onMouseMove"
    />
    <div v-show="false">
      <MapPopup ref="popupRef" :rows="rows" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import mapboxgl, {
  ExpressionSpecification,
  FillExtrusionLayerSpecification,
  GeoJSONSourceSpecification,
  LngLatLike,
  MapMouseEvent
} from 'mapbox-gl'
import { computed, ref, toRefs, watch } from 'vue'
import { useStore } from 'vuex'
import { useGeneralStore } from '@/pinia'
import GeojsonLayer, { GeojsonLayerEvent } from '@/components/map/mapbox-layers/GeojsonLayer.vue'
import MapPopupBuilding from '@/components/map/popups/MapPopupBuilding.vue'
import { formatNumber } from '@/utils/math'
import { CHANGE_BUILDING, RESET_IVION_INSTANCES } from '@/store/action-types'
import { INDOORVIEWER_PREFIX } from '@/store/module-prefixes'
import { SET_CLICKED_ITEM_TYPE } from '@/store/mutation-types'
import { getRealmId } from '@/utils/realmId'
import { useI18n } from 'vue-i18n'
import { ColorScaleConfig, configToMapboxExpression, getColorForValue } from '@/utils/colorScale'
import { LEVEL_PROPERTIES } from '@/interfaces/level.interface'
import { getLevelsv1 } from '@/services/floors.service'
import useMapbox from '@/composables/useMapbox'
import { RGBA } from '@/utils/rgba'
import useColoredEntities from '@/composables/useColoredEntities'
import MapPopup from '../../popups/MapPopup.vue'
import { Row } from '@/components/Builder/BuilderComponent.vue'
import {
  createColorCircleLabel,
  createDivider,
  createSmallSubtitle,
  createSmallTitle,
  createTinyBoldText
} from '@/components/Builder'
import { useCurrentFeatureFilters } from '@/composables/useFilters'
import { useIotCoverageStore } from '@/pinia/iotCoverage'
import { useHoverStore } from '@/pinia/hover'
import { storeToRefs } from 'pinia'
import { useEntityLevel } from '@/composables/useEntityLevel'
import iconSensor from '@/assets/sensor-icon.svg'
import iconIndoorView from '@/assets/icon-indoorview.svg'

const SOURCE_ID = 'source-building'

type Props = {
  before: string
  layerId: string
  source: GeoJSONSourceSpecification | null
}

const { addHoveredItem, removeLastHoveredItem } = useHoverStore()
const piniaStore = useGeneralStore()
const store = useStore()
const filters = useCurrentFeatureFilters()
const { t } = useI18n()
const { map } = useMapbox()
const coloredEntities = useColoredEntities()
const coverage = useIotCoverageStore()
const { enabledEntityLevels } = storeToRefs(useEntityLevel())
const levelIsEnabled = computed(() => enabledEntityLevels.value.includes('LEVEL'))

const props = defineProps<Props>()

const { before, layerId, source } = toRefs(props)
const { selectedAttachment } = storeToRefs(piniaStore)

const COLORS = {
  HOVERED: '#1d234f',
  DEFAULT: '#7B82AF',
  DATA_COMPARISON_LOW: 'rgb(116,180,213)',
  DATA_COMPARISON_MEDIUM: 'rgb(61,239,121)',
  DATA_COMPARISON_HIGH: 'rgb(203,101,101)',
  DATA_COMPARISON_NO_DATA: 'rgb(153,153,153)',
  SELECTED_FILE: 'rgba(250, 219, 20, 0.8)'
}

const hoveredBuildingId = ref<string | null>(null)
const popupTitle = ref<string>('')
const popupRef = ref<InstanceType<typeof MapPopupBuilding>>()
const popup = ref(new mapboxgl.Popup({ closeButton: false, offset: 5 }))
const dataComparisonPopup = ref(new mapboxgl.Popup({ closeButton: false, offset: 5 }))
const hoverRealEstateId = ref<string | null>('')
const hasData = ref(false)
const subtitle = ref('')
const hasIndoorView = ref(false)
const multipleBuildingsLevel = ref<LEVEL_PROPERTIES[]>([])

const icons = computed<Row[]>(() => {
  const icons: any[] = []
  if (hasData.value && hasSensorDataAccess.value) {
    icons.push(iconSensor)
  }
  if (hasIndoorView.value && hasIndoorviewAccess.value) {
    icons.push(iconIndoorView)
  }
  return [
    {
      cols: icons.map((src) => ({
        type: 'image',
        style: {
          width: '30px',
          height: '25px',
          paddingRight: '10px'
        },
        props: {
          src
        }
      }))
    }
  ]
})

const hoveredBuildingValue = computed(
  () => coloredEntities.data?.find((v) => v.id === hoveredBuildingId.value)?.value
)

const colorMeta = computed<Record<string, any>>(
  () =>
    coloredEntities.data?.find((v) => v.id === hoveredBuildingId.value)?.meta ??
    ({} as Record<string, any>)
)
const color = computed(() =>
  getColorForValue(coloredEntities.colorScale, hoveredBuildingValue.value ?? 0)
)

const colorLabel = computed<string | undefined>(() =>
  hoveredBuildingValue.value !== undefined
    ? coloredEntities.title +
      ': ' +
      coloredEntities.formatter(hoveredBuildingValue.value) +
      coloredEntities.unit
    : undefined
)

const colorMetaKeyToLabel = computed<Record<string, string>>(() => ({
  attractiveness: t('general__data__attractiveness'),
  condition: t('general__data__condition'),
  area: t('general__data__area'),
  numberOfSpaces: t('general__data__number-of-spaces')
}))

const colorMetaKeyToFormatter = {
  attractiveness: (x) => x.toString(),
  condition: (x) => x.toString(),
  area: (x) => `${formatNumber(x, 0)} m²`,
  numberOfSpaces: (x) => x.toString()
}

const createColorMetaRows = (colorMeta: Record<string, any>): Row[] => {
  return Object.entries(colorMeta)
    .toSorted((a, b) => b[1] - a[1])
    .map(([key, value]) => {
      const label = colorMetaKeyToLabel.value[key] ? colorMetaKeyToLabel.value[key] : t(key)
      const text = colorMetaKeyToFormatter[key] ? colorMetaKeyToFormatter[key](value) : value
      return createTinyBoldText(label + ': ' + text, {
        style: {
          marginTop: '4px'
        }
      })
    })
}

const rows = computed<Row[]>((): Row[] => [
  ...(coloredEntities.active ? [] : icons.value),
  createSmallTitle(popupTitle.value),
  createSmallSubtitle(
    realmId.value === 'akademiskahus'
      ? t('general__data__marking-building') + ': ' + subtitle.value
      : subtitle.value
      ? t('general__data__marking-label') + ': ' + subtitle.value
      : ''
  ),
  ...(colorLabel.value
    ? [
        createDivider(),
        createColorCircleLabel(color.value.toHex(), colorLabel.value),
        ...(coloredEntities.active ? createColorMetaRows(colorMeta.value) : [])
      ]
    : [])
])

const currentDataComparison = computed<string | null>(() => store.state.currentDataComparison)
const dataComparisonColorScale = computed<ColorScaleConfig>((): ColorScaleConfig => {
  return {
    type: 'STEP',
    cutoffs: [
      { color: new RGBA(116, 180, 213, 1).toHex() },
      { value: store.state.dataComparisonInterval.low, color: new RGBA(61, 239, 121, 1).toHex() },
      { value: store.state.dataComparisonInterval.high, color: new RGBA(203, 101, 101, 1).toHex() }
    ]
  }
})
const filteredMapLayers = computed(() => store.state.mapview.filteredLayers)
const hoveredComplexId = computed<string | null>(() => store.state.mapview.hoveredComplexId ?? null)
const hasIndoorviewAccess = computed<boolean>(() => store.getters.hasIndoorviewAccess)
const hasSensorDataAccess = computed<boolean>(() => store.getters.hasSensorDataAccess)

const selectedComplexId = computed<string | null>(
  () => store.state.mapview.selectedComplexId ?? null
)
const selectedRealEstateId = computed<string | null>(
  () => store.state.mapview.selectedRealEstateId ?? null
)
const selectedBuildingId = computed<string | null>(
  () => store.state.mapview.selectedBuilding?.id ?? null
)

const selectedBuildingsDashboard = computed(
  () => filters.value?.submitted?.buildings.map((b) => b.id) || []
)

const dataComparisonType = computed<string>(() => {
  switch (currentDataComparison.value) {
    case 'BTA':
      return 'bta'
    case 'BRA':
      return 'bra'
    case 'LOA-h':
      return 'loa'
    case 'LOA':
      return 'loa'
    case t('general__data__area-efficiency-loa-h'):
      return 'areaEfficiency'
    case t('general__data__area-efficiency'):
      return 'areaEfficiency'
    default:
      return 'bta'
  }
})
const buildingSource = computed<GeoJSONSourceSpecification | null>(() => {
  if (!source.value) return null
  const newSource: GeoJSONSourceSpecification = {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      // @ts-expect-error - the typing is too general. data.features always exists in this context
      features: source.value.data.features.map((feature) => {
        const newFeature = { ...feature }
        newFeature.properties.hasSensorData = coverage.constructions.includes(feature.properties.id)
        newFeature.properties.coloredBuildingValue = coloredEntities.data?.find(
          (el) => el.id === feature.properties.id
        )?.value
        return newFeature
      })
    }
  }
  return newSource
})
const realmId = computed(() => getRealmId())
const visibility = computed(() => {
  const buildingOption = filteredMapLayers.value.find((el) => el.id === 'building')
  return buildingOption.visible
})
const opacity = computed(() => {
  if (coloredEntities.active) {
    return 1
  } else {
    return 0.7
  }
})
const fillExtrusionColor = computed(() => {
  if (coloredEntities.active) {
    return coloredBuildingsExpression()
  }

  if (store.getters.isDataComparisonToolActive) {
    return dataComparisonExpression()
  }

  if (selectedAttachment.value?.entityId) {
    return attachmentExpression()
  }
  return fallbackExpression()
})

watch(fillExtrusionColor, () => {
  if (!map) return
  map.setPaintProperty(layerId.value, 'fill-extrusion-color', fillExtrusionColor.value)
})
watch(
  selectedBuildingsDashboard,
  async () => {
    await updateLayerMultipleLevel()
  },
  { immediate: true }
)

const layerStyle = computed<FillExtrusionLayerSpecification>(() => {
  const layer: FillExtrusionLayerSpecification = {
    id: layerId.value,
    source: SOURCE_ID,
    type: 'fill-extrusion',
    paint: {
      'fill-extrusion-color': fillExtrusionColor.value,
      'fill-extrusion-height': ['get', 'height'],
      'fill-extrusion-base': 0,
      'fill-extrusion-opacity': opacity.value,
      'fill-extrusion-vertical-gradient': false
    },
    layout: { visibility: visibility.value ? 'visible' : 'none' },
    filter: getFilterExpression(),
    minzoom: 10
  }
  return layer
})

const dispatchBuilding = (payload) => store.dispatch(CHANGE_BUILDING, payload)
const resetBuildingInstances = () =>
  store.dispatch(`${INDOORVIEWER_PREFIX}/${RESET_IVION_INSTANCES}`)
const commitClick = (payload) => store.commit(SET_CLICKED_ITEM_TYPE, payload)

const isMeasurementActive = computed(() => store.getters.isMeasurementActive)
const isDataComparisonToolActive = computed(() => store.getters.isDataComparisonToolActive)
watch(isDataComparisonToolActive, () => {
  popup.value.remove()
})
// Methods
function getFilterExpression(): ExpressionSpecification {
  if (selectedBuildingsDashboard.value.length > 1 && multipleBuildingsLevel.value.length > 0) {
    return ['==', ['index-of', ['get', 'id'], ['literal', selectedBuildingsDashboard.value]], -1]
  }
  if (!levelIsEnabled.value) {
    return ['all']
  }
  return ['!=', ['get', 'id'], selectedBuildingId.value]
}

function attachmentExpression(): ExpressionSpecification {
  return [
    'case',
    ['==', selectedAttachment.value!.entityId, ['get', 'id']],
    COLORS.SELECTED_FILE,
    COLORS.DEFAULT
  ]
}

function coloredBuildingsExpression(): ExpressionSpecification {
  const coloredBuildingsExpression: ExpressionSpecification = configToMapboxExpression(
    'coloredBuildingValue',
    coloredEntities.colorScale,
    new RGBA(123, 130, 175, 1)
  )

  return [
    'case',
    ['!=', ['coalesce', ['feature-state', 'color'], ''], ''],
    ['feature-state', 'color'],
    [
      'case',
      ['!=', ['coalesce', ['feature-state', 'color'], ''], ''],
      ['feature-state', 'color'],
      coloredBuildingsExpression // Fallback to coloredBuildingsExpression if feature-state color is not set
    ]
  ]
}

function dataComparisonExpression(): ExpressionSpecification {
  const dataComparisonExpression: ExpressionSpecification = configToMapboxExpression(
    dataComparisonType.value,
    dataComparisonColorScale.value,
    new RGBA(153, 153, 153, 1)
  )
  return [
    'case',
    ['!=', ['coalesce', ['feature-state', 'color'], ''], ''],
    ['feature-state', 'color'],
    [
      'case',
      ['!=', ['coalesce', ['feature-state', 'color'], ''], ''],
      ['feature-state', 'color'],
      dataComparisonExpression // Fallback to dataComparisonExpression if feature-state color is not set
    ]
  ]
}

function fallbackExpression(): ExpressionSpecification {
  const standardExpression: ExpressionSpecification = [
    'case',
    ['boolean', ['==', hoveredComplexId.value, ['get', 'complexId']], false],
    COLORS.HOVERED,
    ['boolean', ['==', hoverRealEstateId.value, ['get', 'realEstateId']], false],
    COLORS.HOVERED,
    ['==', ['get', 'complexId'], selectedComplexId.value],
    COLORS.HOVERED,
    ['==', ['get', 'realEstateId'], selectedRealEstateId.value],
    COLORS.HOVERED,
    COLORS.DEFAULT
  ]
  return [
    'case',
    ['!=', ['coalesce', ['feature-state', 'color'], ''], ''],
    ['feature-state', 'color'],
    [
      'case',
      ['!=', ['coalesce', ['feature-state', 'color'], ''], ''],
      ['feature-state', 'color'],
      standardExpression // Fallback to standardExpression if feature-state color is not set
    ]
  ]
}

function onMouseEnter() {
  if (store.getters.isMeasurementActive) return
  setMapCursor('pointer')
}

const feature = ref<GeoJSON.Feature<GeoJSON.Geometry>>()

function onMouseMove(e: MapMouseEvent): void {
  if (isFirstLayer(e)) {
    feature.value = e.features?.[0]
    updateHoverId(e)
    return
  }
  resetHoverId()
}
function isFirstLayer(event: GeojsonLayerEvent): boolean {
  const renderedFeatures = map?.queryRenderedFeatures(event.point)
  if (!renderedFeatures?.[0]) return false
  return renderedFeatures[0].layer?.id === layerId.value
}

watch(feature, (newValue, oldValue) => {
  if (!newValue?.properties?.id) {
    removeLastHoveredItem()
    return
  }

  if (oldValue && newValue.properties?.id === oldValue.properties?.id) {
    return
  }

  removeLastHoveredItem()
  if (!isDataComparisonToolActive.value) {
    addHoveredItem(newValue.properties.id, { color: COLORS.HOVERED })
  }
})

function updateHoverId(event: GeojsonLayerEvent): void {
  const features = event.features
  if (!features?.[0]) {
    resetHoverId()
    return
  }
  if (hoveredBuildingId.value === features[0].properties?.id) return
  hoveredBuildingId.value = features[0].properties?.id
  popupTitle.value = features[0].properties?.displayName
  hasData.value = features[0].properties?.hasSensorData
  subtitle.value = features[0].properties?.marking ? features[0].properties.marking : ''
  hasIndoorView.value = features[0].properties?.navvisLinks?.length > 2
  if (isDataComparisonToolActive.value) {
    const coordinates = event.lngLat
    if (features[0].properties)
      showDataComparisonPopup(coordinates, popupTitle.value, features[0].properties)
    return
  }
  showPopup(event.lngLat)
}

function onMouseLeave(): void {
  if (store.getters.isMeasurementActive) return
  setMapCursor('')
  feature.value = undefined
  resetHoverId()
  removeLastHoveredItem()
}

async function onClick(e: MapMouseEvent): Promise<void> {
  const last = e.features?.[0]
  if (last?.layer?.id !== layerId.value) {
    return
  }
  if (isMeasurementActive.value) return
  if (!isFirstLayer(e)) return
  await resetBuildingInstances()
  removeLastHoveredItem()
  // @ts-expect-error - the typing is too general. data.features always exists in this context
  const building = source.value.data.features.find((feature) => feature.id === last.properties?.id)
  if (building) {
    await dispatchBuilding({ building })
    commitClick({ type: 'building' })
  }
}

function showDataComparisonPopup(
  coordinates: LngLatLike,
  title: string,
  {
    bta,
    bra,
    loa,
    areaEfficiency
  }: { title?: string; bta?: number; bra?: number; loa?: number; areaEfficiency?: number }
) {
  if (!map) return

  const btaValue = bta ? formatNumber(bta, 0) : '-'
  const braValue = bra ? formatNumber(bra, 0) : '-'
  const loaValue = loa ? formatNumber(loa, 0) : '-'
  const areaEfficiencyValue = areaEfficiency ? formatNumber(areaEfficiency, 1) : '-'

  const unitSquareMeter = t('general__data__unit-square-meter')
  const areaEfficiencyPlain = t('general__data__area-efficiency-plain')
  const loaBtaRatio = realmId.value === 'akademiskahus' ? '(LOA-h/BTA)' : '(LOA/BTA)'

  const html = `
    <h3>${title}</h3>
    <hr>
    <h4 style='text-align: left;'>BTA:<span style='float: right;'>${btaValue} ${unitSquareMeter}</span></h4>
    <h4 style='text-align: left;'>BRA:<span style='float: right;'>${braValue} ${unitSquareMeter}</span></h4>
    <h4 style='text-align: left;'>LOA-h:<span style='float: right;'>${loaValue} ${unitSquareMeter}</span></h4>
    <h4 style='text-align: left; margin-bottom: -1px;'>${areaEfficiencyPlain}: <span style='float: right'>${areaEfficiencyValue} %</span></h4>
    <h5 style='text-align: left; margin-top: 0;'>${loaBtaRatio}</h5>
  `

  dataComparisonPopup.value.setLngLat(coordinates).setHTML(html).addTo(map)
  dataComparisonPopup.value.trackPointer()
}

function showPopup(lngLat: mapboxgl.LngLat): void {
  if (!popupRef.value || !map) return
  popup.value.setLngLat(lngLat).setDOMContent(popupRef.value.$el).addTo(map)
  popup.value.trackPointer()
}

function setMapCursor(type: string): void {
  if (!map) return
  const canvas = map.getCanvas()
  canvas.style.cursor = type
}

function resetHoverId() {
  hoveredBuildingId.value = null
  popup.value.remove()
  dataComparisonPopup.value.remove()
}
async function updateLayerMultipleLevel() {
  if (selectedBuildingsDashboard.value.length <= 1) {
    multipleBuildingsLevel.value = []
    return null
  }
  selectedBuildingsDashboard.value.forEach(async (building: any) => {
    const levelForBuilding = await getLevelsv1(building)
    if (!levelForBuilding) return
    multipleBuildingsLevel.value.push(...levelForBuilding)
  })
}
</script>
